/**
 * HIS V4 · ข้อมูลตัวอย่างสำหรับ Patient Panel และหน้าตรวจรักษา
 * ข้อความ ค่า และรายการทั้งชุดคัดลอกจาก ui-v4-design.html (PatientWorkspace / PatientDoctor)
 * เพื่อใช้ปิดช่องว่างระหว่าง Patient Queue กับหน้าจริง ยังไม่ต่อ API
 */

import type { Point, Stroke } from "./examModelV4";
import type { FlagKey, PatientFlag } from "./PatientPanelV4";

export type { FlagKey, PatientFlag };

export type IcdEntry = { code: string; term: string };

export type ChipTone = "neutral" | "warn" | "danger";

export type SummaryChip = { text: string; tone: ChipTone };

export type PeKey = "cvs" | "resp" | "eent" | "abd" | "msk" | "neuro" | "skin";

export type PeDef = { key: PeKey; label: string; placeholder: string };

/** ชื่อ field ตรงกับ selectedPatient */
export type MockPatient = {
  hn: string;
  full_name: string;
  gender_name: string;
  full_age: string;
  birthdate: string;
  present_address: { tel_mobile: string };
  citizen_no: string;
  citizen_passport?: string;
  coverage: string;
  profile_image?: { image?: string } | null;
  nextAppointment: string;
  allergy: string;
  membership: string;
  photoCaption: string;
  flags: PatientFlag[];
  summary: SummaryChip[];
};

/** ชื่อ field ตรงกับ selectedEncounter */
export type MockEncounter = {
  id?: unknown;
  number: string;
  status?: string;
  status_name: string;
};

/** ICD-10 ชุดตัวอย่างตามที่ design ใช้ค้นหา (15 รายการ) */
export const MOCK_ICD10: IcdEntry[] = [
  { code: "M54.5", term: "Low back pain (ปวดหลังส่วนล่าง)" },
  { code: "M54.2", term: "Cervicalgia (ปวดคอ)" },
  { code: "M25.5", term: "Pain in joint (ปวดข้อ)" },
  { code: "M79.1", term: "Myalgia (ปวดกล้ามเนื้อ)" },
  { code: "M75.1", term: "Rotator cuff syndrome (ไหล่)" },
  { code: "M17.9", term: "Gonarthrosis, unspecified (ข้อเข่าเสื่อม)" },
  { code: "J06.9", term: "Acute upper respiratory infection (ติดเชื้อทางเดินหายใจส่วนบน)" },
  { code: "K29.7", term: "Gastritis, unspecified (กระเพาะอาหารอักเสบ)" },
  { code: "I10", term: "Essential hypertension (ความดันโลหิตสูง)" },
  { code: "E11.9", term: "Type 2 diabetes mellitus (เบาหวานชนิดที่ 2)" },
  { code: "L70.0", term: "Acne vulgaris (สิว)" },
  { code: "D10.0", term: "Benign neoplasm of lip (เนื้องอกชนิดไม่ร้ายที่ริมฝีปาก)" },
  { code: "R51", term: "Headache (ปวดศีรษะ)" },
  { code: "N39.0", term: "Urinary tract infection (ติดเชื้อทางเดินปัสสาวะ)" },
  { code: "Z09", term: "Follow-up examination after treatment (ตรวจติดตามหลังการรักษา)" },
];

export const PE_DEFS: PeDef[] = [
  { key: "cvs", label: "Cardiovascular", placeholder: "เช่น หัวใจเต้นสม่ำเสมอ ไม่มี murmur" },
  { key: "resp", label: "Respiratory", placeholder: "เช่น เสียงลมเข้าปอดสองข้างเท่ากัน" },
  { key: "eent", label: "EENT", placeholder: "ตา หู คอ จมูก" },
  { key: "abd", label: "Abdomen", placeholder: "เช่น นุ่ม ไม่มีกดเจ็บ" },
  { key: "msk", label: "Musculoskeletal", placeholder: "เช่น กดเจ็บบริเวณ L4-5" },
  { key: "neuro", label: "Neurological", placeholder: "เช่น แรงกล้ามเนื้อ 5/5" },
  { key: "skin", label: "Skin", placeholder: "ผื่น แผล รอยโรค" },
];

export const PE_OPTIONS = ["-", "ปกติ", "ผิดปกติ"] as const;

const FLAG_DEFS: PatientFlag[] = [
  {
    key: "src",
    label: "ช่องทางที่รู้จักคลินิก",
    glyph: "i",
    text: "Facebook",
  },
  {
    key: "req",
    label: "Request พิเศษ",
    glyph: "!",
    text: "ขอเจ้าหน้าที่หญิง",
  },
  {
    key: "alg",
    label: "ประวัติแพ้",
    glyph: "แพ้",
    text: "ยา: Penicillin (ผื่น)",
  },
  {
    key: "chr",
    label: "โรคประจำตัว",
    glyph: "โรค",
    text: "ภูมิแพ้อากาศ",
  },
];

const SUMMARY: SummaryChip[] = [
  { text: "อุณหภูมิ 36.8 °C", tone: "neutral" },
  { text: "BP 120/78", tone: "neutral" },
  { text: "ชีพจร 76", tone: "neutral" },
  { text: "O2 Sat 99%", tone: "neutral" },
  { text: "Pain 4/10", tone: "warn" },
  { text: "Triage 4 · ไม่รุนแรง", tone: "neutral" },
  { text: "แพ้ Penicillin (ผื่น)", tone: "danger" },
  { text: "ภูมิแพ้อากาศ (โรคประจำตัว)", tone: "neutral" },
];

const BASE_PATIENTS: Record<string, MockPatient> = {
  "260001": {
    hn: "260001",
    full_name: "น.ส. น้ำหนึ่ง ทดสอบ",
    gender_name: "หญิง",
    full_age: "29 ปี",
    birthdate: "05/04/2540",
    present_address: { tel_mobile: "0890000001" },
    citizen_no: "1234567890123",
    coverage: "เงินสด",
    nextAppointment: "-",
    allergy: "แพ้: Penicillin (ผื่น)",
    membership: "ยังไม่เป็นสมาชิก",
    photoCaption: "รูปจากบัตรประชาชน",
    flags: FLAG_DEFS,
    summary: SUMMARY,
  },
  "260002": {
    hn: "260002",
    full_name: "คุณ กรีนที ทดสอบ",
    gender_name: "ไม่ระบุ",
    full_age: "34 ปี",
    birthdate: "12/08/2535",
    present_address: { tel_mobile: "0890000002" },
    citizen_no: "1234567890124",
    coverage: "เงินสด",
    nextAppointment: "-",
    allergy: "ยังไม่บันทึกการแพ้",
    membership: "ยังไม่เป็นสมาชิก",
    photoCaption: "รูปจากบัตรประชาชน",
    flags: FLAG_DEFS,
    summary: SUMMARY,
  },
};

const text = (value: unknown, fallback: string): string =>
  typeof value === "string" && value.trim() !== "" ? value : fallback;

/** แถวคิวมีแค่ hn / patient_name ส่วนที่เหลือเป็นข้อมูลตัวอย่าง */
export function mockPatientFor(row: unknown): MockPatient {
  const source: Record<string, unknown> =
    row !== null && typeof row === "object" ? (row as Record<string, unknown>) : {};
  const hn = text(source.hn, "260001");
  const base = BASE_PATIENTS[hn] ?? BASE_PATIENTS["260001"];

  return { ...base, hn, full_name: text(source.patient_name, base.full_name) };
}

export function mockEncounterFor(row: unknown): MockEncounter {
  const source: Record<string, unknown> =
    row !== null && typeof row === "object" ? (row as Record<string, unknown>) : {};

  return {
    id: source.id,
    number: text(source.number, "177"),
    status: typeof source.status === "string" ? source.status : undefined,
    status_name: text(source.status_name, "กำลังพบแพทย์"),
  };
}

export const mockPatients: MockPatient[] = Object.values(BASE_PATIENTS).map((patient) =>
  mockPatientFor({ hn: patient.hn }),
);

/** ค้นหา ICD-10 แบบเดียวกับ design: match code หรือ term, ไม่เอารหัสที่ใช้ไปแล้ว, เอาสูงสุด 6 รายการ */
export function searchIcd10(
  query: string,
  usedCodes: string[],
  limit = 6,
): IcdEntry[] {
  const needle = query.trim().toLowerCase();
  if (needle === "") return [];
  return MOCK_ICD10.filter(
    (entry) =>
      usedCodes.indexOf(entry.code) < 0 &&
      (entry.code.toLowerCase().indexOf(needle) >= 0 ||
        entry.term.toLowerCase().indexOf(needle) >= 0),
  ).slice(0, limit);
}

/** เวลา HH:mm จาก ISO string ที่ sequence ตอบกลับมา (savedAt) */
export const timeOfIso = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

export const timeOfNow = (): string => {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

/* ------------------------------------------------------------------
 * Template ตรวจร่างกาย (design มี onTpl/tplOpts ใน Component ของ PatientDoctor)
 * ------------------------------------------------------------------ */

export const PE_TEMPLATE_OPTIONS = ["-", "ปกติทุกระบบ", "ปวดหลัง / กล้ามเนื้อและกระดูก", "ล้างค่า"] as const;

export type PeValues = { sel: Record<PeKey, string>; text: Record<PeKey, string> };

const emptyPeRecord = (): Record<PeKey, string> =>
  PE_DEFS.reduce((acc, def) => ({ ...acc, [def.key]: "" }), {} as Record<PeKey, string>);

export function peTemplateValues(option: string): PeValues {
  const sel = emptyPeRecord();
  const text = emptyPeRecord();

  if (option === "ปกติทุกระบบ") {
    PE_DEFS.forEach((def) => {
      sel[def.key] = "ปกติ";
      text[def.key] = "ปกติ";
    });
  }

  if (option === "ปวดหลัง / กล้ามเนื้อและกระดูก") {
    sel.msk = "ผิดปกติ";
    sel.neuro = "ปกติ";
    text.msk = "กดเจ็บบริเวณหลังส่วนล่าง ช่วงการเคลื่อนไหวจำกัดเล็กน้อย";
    text.neuro = "แรงกล้ามเนื้อ 5/5 ความรู้สึกปกติ";
  }

  return { sel, text };
}

/* ------------------------------------------------------------------
 * Image Gallery (6c ของ ตรวจรักษา) · ค่าเหมือน Component ของ PatientGallery
 * ------------------------------------------------------------------ */

/* Point/Stroke เป็น type ของ domain (examModelV4) mock แค่ re-export ให้ UI ใช้งานต่อได้ */
export type { Point, Stroke };

export type GalleryPhoto = {
  label: string;
  caption: string;
  at: string;
  by: string;
  strokes: Stroke[];
  editedAt?: string;
  /** id ของภาพใน API (live mode) */
  id?: number;
  /** data URI / URL ของรูปจริง (live mode) */
  image?: string;
};

export type GalleryEncounter = { encounter: string; at: string; photos: GalleryPhoto[] };

export type GalleryType = { value: string; gradientFrom: string; gradientTo: string; emoji: string };

export type Segment =
  | { kind: "dot"; key: string; left: number; top: number; size: number; color: string }
  | {
      kind: "line";
      key: string;
      left: number;
      top: number;
      length: number;
      thickness: number;
      angle: number;
      color: string;
    };

/** ขนาดภาพต้นทางที่ใช้ทั้ง thumbnail (scale 0.29) และภาพใน modal */
export const CANVAS_WIDTH = 560;
export const CANVAS_HEIGHT = 360;
export const THUMB_SCALE = 0.29;

export const GALLERY_TYPES: GalleryType[] = [
  { value: "ภาพบริเวณที่ตรวจ", gradientFrom: "#F6D9C8", gradientTo: "#E9A98C", emoji: "🩹" },
  { value: "ภาพก่อน-หลังหัตถการ", gradientFrom: "#D6E8F5", gradientTo: "#8DB9DE", emoji: "🩺" },
  { value: "ผลตรวจภายนอก/เอกสาร", gradientFrom: "#EDE7C8", gradientTo: "#CFC48C", emoji: "📄" },
  { value: "อื่น ๆ", gradientFrom: "#E1E8EA", gradientTo: "#B2C0C5", emoji: "🖼" },
];

export const GALLERY_COLORS = ["#D93025", "#1F6FB5", "#1B1B1B", "#1E8E3E"];

export const GALLERY_WIDTHS: { width: number; label: string }[] = [
  { width: 3, label: "บาง" },
  { width: 8, label: "หนา" },
];

export const GALLERY_AUTHOR = "พญ. ตัวอย่าง";

/** ภาพของ Encounter ก่อนหน้า ใช้แสดงในปุ่ม 🕘 History */
export const MOCK_GALLERY_HISTORY: GalleryEncounter[] = [
  {
    encounter: "Encounter 168",
    at: "15/09/2569",
    photos: [
      {
        label: "ภาพบริเวณที่ตรวจ",
        caption: "ผื่นแขนขวา",
        at: "15/09/2569 10:12",
        by: "พญ. ตัวอย่าง",
        strokes: [
          {
            color: "#D93025",
            width: 4,
            points: [[200, 120], [260, 100], [330, 130], [300, 200], [220, 190], [200, 120]],
          },
        ],
      },
      { label: "ภาพก่อน-หลังหัตถการ", caption: "ก่อนทำ", at: "15/09/2569 10:40", by: "พว. ตัวอย่าง", strokes: [] },
    ],
  },
  {
    encounter: "Encounter 155",
    at: "02/08/2569",
    photos: [{ label: "ผลตรวจภายนอก/เอกสาร", caption: "ผลเลือดจากรพ.อื่น", at: "02/08/2569 14:05", by: "พว. ตัวอย่าง", strokes: [] }],
  },
  {
    encounter: "Encounter 142",
    at: "12/07/2569",
    photos: [
      {
        label: "ภาพบริเวณที่ตรวจ",
        caption: "หลังส่วนล่าง",
        at: "12/07/2569 09:30",
        by: "พญ. ตัวอย่าง",
        strokes: [{ color: "#1F6FB5", width: 3, points: [[150, 250], [250, 220], [350, 240]] }],
      },
    ],
  },
];

export function galleryType(label: string): GalleryType {
  return GALLERY_TYPES.find((type) => type.value === label) ?? GALLERY_TYPES[GALLERY_TYPES.length - 1];
}

/** แปลงเส้นที่วาดเป็น segment เรขาคณิตให้ component render เป็น div (design ใช้ div ไม่ใช่ canvas) */
export function strokeSegments(strokes: Stroke[]): Segment[] {
  const out: Segment[] = [];

  strokes.forEach((stroke, strokeIndex) => {
    const thickness = stroke.width || 3;

    stroke.points.forEach((point, pointIndex) => {
      if (pointIndex === stroke.points.length - 1) return;
      const next = stroke.points[pointIndex + 1];
      const dx = next[0] - point[0];
      const dy = next[1] - point[1];
      out.push({
        kind: "line",
        key: `${strokeIndex}-${pointIndex}`,
        left: point[0],
        top: point[1] - thickness / 2,
        length: Math.sqrt(dx * dx + dy * dy) + 0.5,
        thickness,
        angle: (Math.atan2(dy, dx) * 180) / Math.PI,
        color: stroke.color,
      });
    });

    if (stroke.points.length === 1) {
      const [x, y] = stroke.points[0];
      out.push({
        kind: "dot",
        key: `${strokeIndex}-dot`,
        left: x - thickness / 2,
        top: y - thickness / 2,
        size: thickness,
        color: stroke.color,
      });
    }
  });

  return out;
}

/** กันค่าพิกัดเสีย (เช่น NaN จาก rect ความกว้าง 0) ไม่ให้หลุดเข้า style */
export const clampPoint = (x: number, y: number): Point => {
  const nx = Number.isFinite(x) ? Math.round(x) : 0;
  const ny = Number.isFinite(y) ? Math.round(y) : 0;
  return [Math.max(0, Math.min(CANVAS_WIDTH, nx)), Math.max(0, Math.min(CANVAS_HEIGHT, ny))];
};

/* ------------------------------------------------------------------
 * บันทึกการพยาบาล (6b ของ ตรวจรักษา) · ค่าเหมือน Component ของ PatientNursing
 * ------------------------------------------------------------------ */

export type NurseTemplate = { name: string; fields: string[] };

export const NURSE_TEMPLATES: NurseTemplate[] = [
  { name: "A-I-E", fields: ["Assessment (A)", "Intervention (I)", "Evaluation (E)"] },
  { name: "Focus Charting (DAR)", fields: ["Data (D)", "Action (A)", "Response (R)"] },
  { name: "ข้อความอิสระ", fields: ["บันทึก"] },
];

export const NURSE_DISCIPLINES = ["แพทย์", "พยาบาล", "เภสัชกร", "นักกายภาพบำบัด", "นักโภชนาการ", "อื่น ๆ"];

export type NurseNote = { text: string; template: string; by: string; at: string };

export type DisciplineNote = { discipline: string; text: string; by: string; at: string };

export const NURSE_AUTHOR = "พยาบาลตัวอย่าง 1";

export const MOCK_NURSE_NOTES: NurseNote[] = [
  {
    text: "วัด Vital Sign แล้ว อธิบายขั้นตอนการตรวจให้ผู้ป่วยทราบ ผู้ป่วยให้ความร่วมมือดี",
    template: "",
    by: NURSE_AUTHOR,
    at: "27/10/2569 10:50",
  },
];

export const MOCK_DISCIPLINE_NOTES: DisciplineNote[] = [
  {
    discipline: "พยาบาล",
    text: "ผู้ป่วยแพ้ Penicillin แจ้งแพทย์แล้ว",
    by: NURSE_AUTHOR,
    at: "27/10/2569 10:52",
  },
];

/** วันเวลาแบบไทย (พ.ศ.) แบบที่ design ใช้ในรายบันทึกใหม่ */
export const dateTimeOfNow = (): string => {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, "0");
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const yyyy = String(now.getFullYear() + 543);
  return `${dd}/${mm}/${yyyy} ${timeOfNow()}`;
};
